/**
 * Which events of a board are in (or near) the viewport — the input to
 * board viewport streaming (`boardViewportStreamEnabled`): the gateway holds
 * every board row and the board names the rows it draws, so a tab receives
 * the games on screen, not the sport.
 *
 * One IntersectionObserver per table over `tr[data-st-event]` (the
 * `useLt2Card` pattern), overscan by `rootMargin` so a scroll of a game or
 * two never shows a frozen row. Page scroll is the root: the board is one
 * `<table>` in the window, not an inner scroller.
 *
 * FAIL WIDE: no IntersectionObserver (jsdom, an old engine) ⇒ `null` = "every
 * row", which is exactly today's behaviour — never a dark board.
 */
import { useEffect, useRef, type RefObject } from 'react';

export const VIEWPORT_ROOT_MARGIN = '600px 0px';

export function useVisibleEventKeys(
  container: RefObject<HTMLElement | null>,
  /** Changes when the set/order of rows changes → re-observe. */
  rowsKey: string,
  onChange: ((keys: Set<string> | null) => void) | undefined,
): void {
  const cb = useRef(onChange);
  cb.current = onChange;
  const enabled = onChange != null;
  useEffect(() => {
    if (!enabled) return;
    const root = container.current;
    if (typeof IntersectionObserver !== 'function' || !root) { cb.current?.(null); return; }
    const visible = new Set<string>();
    let lastKey = '';
    const publish = () => {
      const k = [...visible].sort().join('|');
      if (k === lastKey) return;
      lastKey = k;
      cb.current?.(new Set(visible));
    };
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const key = (e.target as HTMLElement).dataset.stEvent;
          if (!key) continue;
          if (e.isIntersecting) visible.add(key); else visible.delete(key);
        }
        publish();
      },
      { root: null, rootMargin: VIEWPORT_ROOT_MARGIN, threshold: 0 },
    );
    for (const el of root.querySelectorAll('tr[data-st-event]')) io.observe(el);
    return () => io.disconnect();
  }, [container, rowsKey, enabled]);
}
